/* eslint-disable max-len */
import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCheckout } from "../../context/CheckoutContext";
import ApiService from "../../service/ApiService";
import styles from "./CheckoutPagamentoCartaoProc.module.css";
import { Header } from "../../components/Header";
import CheckoutPagamentoBoletoCod from "../CheckoutPaymentPix/CheckoutPagamentoBoletoCod";
import { createCommission, createSaleData, formatPaymentData } from "../../helpers/SalesFunctions";
import General from "../../helpers/General";
import { loadPagSeguroSDK } from "../../utils/pagseguro";
import { initializeClearSaleFingerprint } from "../../utils/clearsale";
import { ecommerceEvents } from "../../utils/gtm";
import { trackPurchase } from "../../utils/affiliateAnalytics";

const apiService = new ApiService();
const CheckoutPagamentoCartaoProc: FunctionComponent = () => {
  const {
    cart,
    purchaser,
    address,
    card,
    clientAndAffiliate,
    paymentType,
    coupon,
    cleanCart,
    paytimeInfo,
    planId,
    totalPrice,
    planName,
    setCard,
    abandonedCartId
  } = useCheckout();
  const navigate = useNavigate();
  const params = useParams();
  const [boletoData, setBoletoData] = useState({});

  const finalPrice = paymentType === 'Pix'
    ? Math.round((totalPrice * (1 - 0.05)) * 100) / 100
    : totalPrice;

  useEffect(() => {
    if (!card.security_code && paymentType === 'Cartão') {
      params.slug? navigate(`/checkout-pagamento/${params.slug}`) : navigate(`/checkout-pagamento`);
      return;
    }

    if (cart.length === 0) {
      params.slug ? navigate(`/${params.slug}`) : navigate("/");
      return;
    }

    processPayment();
  }, []);

  const processPayment = async () => {
    const aux = General.formatCurrency(finalPrice * 100);
    const splitTotalPrice = aux.replace(/,/g, '');
    const formattedTotalPrice = parseFloat(splitTotalPrice);

    address.cep.replace(/\D/g, "");

    // Common payment data
    const basePaymentData = {
      address: address,
      purchaser: {
        ...purchaser,
        documentNumber: purchaser.document_number,
      },
      payment: {
        type: paymentType,
      },
      products: cart.map(item => ({
        id: item.products_id,
        description: item.description,
        name: item.name,
        quantity: item.quantity,
        price: parseFloat(item.price),
        planName,
      })),
      metadata: {
        planId,
        couponId: coupon.found ? coupon.found.id : null,
        clientAndAffiliate,
        fingerprintSessionId: "",
      },
      abandonedCartId,
    };

    let fingerprintSessionId;
    let paymentDataWithFingerprint;

    try {
      let paymentResponse;

      
      switch(paymentType) {
        case 'Cartão':
          // Inicializa o fingerprint para gerar o sessionID do ClearSale
          fingerprintSessionId = await initializeClearSaleFingerprint();

          // Adiciona o sessionID nos dados de pagamento
          paymentDataWithFingerprint = {
            ...basePaymentData,
            card: {
              holder_name: card.holder_name,
              expiration_month: card.expiration_date.split("/")[0],
              expiration_year: `20${card.expiration_date.split("/")[1]}`,
              card_number: parseInt(card.card_number.split(" ").join("")),
              security_code: card.security_code,
              installments: card.installment
            },
            metadata: {
              ...basePaymentData.metadata,
              fingerprintSessionId
            }
          };
          paymentResponse = await apiService.completePaymentCredit(paymentDataWithFingerprint);

          if (paymentResponse.gateway_authorization === "PAYTIME" && paymentResponse.antifraud[0].analyse_status === "WAITING_AUTH") {
            const request = formatPaymentData(
              paymentResponse,
              card.card_number.split(" ").join("")
            );

            await loadPagSeguroSDK();

            let pagSeguroResponse;

            window.PagSeguro.setUp({
              session: paymentResponse.antifraud[0].session,
              env: "PROD",
            });
            await window.PagSeguro.authenticate3DS(request).then( async result => {
              pagSeguroResponse = result;
              this.logResponseToScreen(result);
              this.stopLoading();  
            }).catch((err) => {  
              if(err instanceof window.PagSeguro.PagSeguroError ) {  
                  console.log(err);  
                  console.log(err.detail);  
                  this.stopLoading();  
              }  
            })

            await apiService.completePaytimeAuth({
            payment: paymentDataWithFingerprint,  
            auth: {
              id: pagSeguroResponse.id,
              status: pagSeguroResponse.status,
              transaction_id: paymentResponse._id,
            }});
          }
          setCard({...card, security_code: ''});
          break;
        
        case 'Boleto':
          paymentResponse = await apiService.completePaymentBankSlip(basePaymentData);
          if (paymentResponse.status === 'Pendente') {
            setBoletoData(paymentResponse);
          }
          break;
        
        case 'Pix':
          paymentResponse = await apiService.completePaymentPix(basePaymentData);
          if (paymentResponse.status === 'Pendente') {
            setBoletoData(paymentResponse);
          }
          break;
      }

      if (paymentResponse.status === 'Pendente' || paymentResponse.status === 'Aprovado') {        
        const transactionId = paymentResponse.id || paymentResponse._id;

        const payload = {
          currency: "BRL",
          value: totalPrice,
          shipping_tier: "free",
          coupon: coupon.name? coupon.name : "",
          email: purchaser.email,
          phone: purchaser.telephone.replace(/[()\-\s]/g, ''),
          first_name: purchaser.name,
          last_name: purchaser.lastname,
          transaction_id: transactionId,
          afiliado: params.slug? params.slug : clientAndAffiliate.affiliate_id? clientAndAffiliate.affiliate_id : "",
          payment_type: "Cartão",
          items: cart.map(item => ({
            item_id: item.id,
            item_name: item.name,
            price: item.price,
            quantity: item.quantity,
            promotion_id: planId,
            promotion_name: planName,
          })),
          shipping_address: {
            city: address.cidade,
            street: address.logradouro,
            number: address.numero,
            state: address.estado,
            neighborhood: address.bairro,
            postal_code: address.cep,
            complement: address.complemento,
          }
        };

        ecommerceEvents.purchase(payload);
        trackPurchase(payload);           
        if (paymentType === 'Cartão') {
          navigate(params.slug ? `/checkout-pagamento-aprovado/${params.slug}` : "/checkout-pagamento-aprovado");
        }
      } else {
        navigate(params.slug ? `/checkout-pagamento-erro/${params.slug}` : "/checkout-pagamento-erro");
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      navigate(params.slug ? `/checkout-pagamento-erro/${params.slug}` : "/checkout-pagamento-erro");
    }
  };
  return (
    <>
      {['Pix', 'Boleto'].includes(paymentType) ? (
        <CheckoutPagamentoBoletoCod boletoData={boletoData} />
      ) : (
        <div className={styles.checkoutPagamentoCartaoProc}>
          <div className={styles.header}>
            <Header path="/checkout-pagamento" />
          </div>
          <div className={styles.processando}>
            <img
              className={styles.httpslottiefilescomanimatIcon}
              alt=""
              src="/httpslottiefilescomanimationspaymentm3qznrce9o@2x.png"
            />
            <div className={styles.texto}>
              <div className={styles.estamosProcessandoSeu}>
                Estamos processando seu pagamento
              </div>
              <div className={styles.oProcessoPoder}>
                O processo poderá demorar alguns instantes.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckoutPagamentoCartaoProc;